import {computed, ref} from 'vue'
import {defineStore} from 'pinia'
import {cpApi} from '@/api'
import type {ModelRole, ModelRoleConfig, ModelSettingsResponse, ReasoningEffort} from '@shared/llm_types'

function errorMessage(cause: unknown, fallback: string): string {
  const response = (cause as {response?: {data?: {error?: unknown}}})?.response?.data?.error
  return typeof response === 'string' ? response : cause instanceof Error ? cause.message : fallback
}

export const useModelSettingsStore = defineStore('modelSettings', () => {
  const catalog = ref<ModelSettingsResponse['catalog']>({lastSuccessfulRefreshAt: null, modelCount: 0, lastError: null})
  const roles = ref<ModelRoleConfig[]>([])
  const isLoading = ref(false)
  const isRefreshing = ref(false)
  const savingRole = ref<ModelRole | null>(null)
  const error = ref<string | null>(null)
  const isEmpty = computed(() => !isLoading.value && catalog.value.modelCount === 0)

  function apply(response: ModelSettingsResponse): void {
    catalog.value = response.catalog
    roles.value = response.roles
  }

  async function load(): Promise<void> {
    isLoading.value = true
    error.value = null
    try { apply(await cpApi.modelSettings.get()) } catch (cause) { error.value = errorMessage(cause, 'Failed to load model settings') }
    finally { isLoading.value = false }
  }

  async function refresh(): Promise<void> {
    isRefreshing.value = true
    error.value = null
    try { apply(await cpApi.modelSettings.refresh()) } catch (cause) { error.value = errorMessage(cause, 'Failed to refresh catalog') }
    finally { isRefreshing.value = false }
  }

  async function save(role: ModelRole, model: string | null, reasoning: ReasoningEffort | null): Promise<boolean> {
    savingRole.value = role
    error.value = null
    try {
      const updated = await cpApi.modelSettings.saveRole(role, {model, reasoning})
      const index = roles.value.findIndex((item) => item.role === role)
      if (index >= 0) roles.value[index] = updated
      return true
    } catch (cause) { error.value = errorMessage(cause, 'Failed to save model settings') }
    finally { savingRole.value = null }
    return false
  }

  return {catalog, roles, isLoading, isRefreshing, savingRole, error, isEmpty, load, refresh, save}
})
